import { AuthConfig, AuthProvider, StorageConfig, BillingConfig, ApiKeyConfig, SessionConfig, OrganizationConfig, EventConfig } from './types'

export function defineConfig(config: AuthConfig): AuthConfig {
  // Validate required fields
  if (!config.appUrl) {
    throw new Error('appUrl is required')
  }

  // Inject default provider if none specified
  const providers: AuthProvider[] = (config.providers && config.providers.length > 0)
    ? config.providers
    : [createEmailProvider({ id: 'email', from: 'no-reply@example.com' })]

  if (!providers || providers.length === 0) {
    throw new Error('At least one provider is required')
  }

  if (!config.storage) {
    throw new Error('storage configuration is required')
  }

  if (!config.apiKeys) {
    throw new Error('apiKeys configuration is required')
  }

  if (!config.sessions) {
    throw new Error('sessions configuration is required')
  }

  // Set defaults
  const defaultConfig: AuthConfig = {
    ...config,
    providers,
    apiKeys: {
      ...config.apiKeys,
      prefix: config.apiKeys.prefix ?? 'kit',
      hash: {
        algo: config.apiKeys.hash?.algo ?? 'argon2id',
        timeCost: config.apiKeys.hash?.timeCost ?? 2,
        memory: config.apiKeys.hash?.memory ?? 19456,
        parallelism: config.apiKeys.hash?.parallelism ?? 1
      },
      defaultExpiry: config.apiKeys.defaultExpiry ?? 365 * 24 * 60 * 60 // 1 year
    },
    sessions: {
      ...config.sessions,
      cookieName: config.sessions.cookieName ?? 'kitium.session',
      ttlSeconds: config.sessions.ttlSeconds ?? 7 * 24 * 60 * 60, // 7 days
      secure: config.sessions.secure ?? true,
      httpOnly: config.sessions.httpOnly ?? true,
      sameSite: config.sessions.sameSite ?? 'lax'
    },
    orgs: {
      enabled: config.orgs?.enabled ?? true,
      defaultRole: config.orgs?.defaultRole ?? 'member',
      maxSeats: config.orgs?.maxSeats ?? 100,
      ...config.orgs
    },
    events: {
      events: config.events?.events ?? ['user.created', 'user.updated', 'org.created', 'org.updated', 'apikey.created', 'apikey.revoked', 'subscription.created', 'subscription.updated'],
      ...config.events
    }
  }

  return defaultConfig
}

// Helper function to create provider configurations
export function createProvider(provider: AuthProvider): AuthProvider {
  const defaults: Partial<AuthProvider> = {
    scopes: [],
    metadata: {}
  }

  return { ...defaults, ...provider }
}

// Helper function to create OAuth provider
export function createOAuthProvider(config: {
  id: string
  clientId: string
  clientSecret: string
  redirectUri?: string
  scopes?: string[]
  metadata?: Record<string, any>
}): AuthProvider {
  return createProvider({
    id: config.id,
    type: 'oauth',
    clientId: config.clientId,
    clientSecret: config.clientSecret,
    redirectUri: config.redirectUri,
    scopes: config.scopes || [],
    metadata: config.metadata
  })
}

// Helper function to create email provider
export function createEmailProvider(config: {
  id: string
  from: string
  metadata?: Record<string, any>
}): AuthProvider {
  return createProvider({
    id: config.id,
    type: 'email',
    metadata: {
      from: config.from,
      ...config.metadata
    }
  })
}

// Helper function to create storage configuration
export function createStorageConfig(config: StorageConfig): StorageConfig {
  const defaults: Partial<StorageConfig> = {
    options: {}
  }

  return { ...defaults, ...config }
}

// Helper function to create billing configuration
export function createBillingConfig(config: BillingConfig): BillingConfig {
  if (!config.products || Object.keys(config.products).length === 0) {
    throw new Error('At least one product must be defined in billing configuration')
  }

  return config
}

// Helper function to create API key configuration
export function createApiKeyConfig(config: ApiKeyConfig): ApiKeyConfig {
  const defaults: ApiKeyConfig = {
    prefix: 'kit',
    hash: {
      algo: 'argon2id',
      timeCost: 2,
      memory: 19456,
      parallelism: 1
    },
    defaultExpiry: 365 * 24 * 60 * 60 // 1 year
  }

  return { ...defaults, ...config }
}

// Helper function to create session configuration
export function createSessionConfig(config: SessionConfig): SessionConfig {
  const defaults: SessionConfig = {
    cookieName: 'kitium.session',
    ttlSeconds: 7 * 24 * 60 * 60, // 7 days
    secure: true,
    httpOnly: true,
    sameSite: 'lax'
  }

  return { ...defaults, ...config }
}

// Helper function to create organization configuration
export function createOrganizationConfig(config: OrganizationConfig): OrganizationConfig {
  const defaults: OrganizationConfig = {
    enabled: true,
    defaultRole: 'member',
    maxSeats: 100
  }

  return { ...defaults, ...config }
}

// Helper function to create event configuration
export function createEventConfig(config: EventConfig): EventConfig {
  const defaults: EventConfig = {
    events: ['user.created', 'user.updated', 'org.created', 'org.updated', 'apikey.created', 'apikey.revoked', 'subscription.created', 'subscription.updated']
  }

  return { ...defaults, ...config }
}

// Validation functions
export function validateConfig(config: AuthConfig): string[] {
  const errors: string[] = []

  // Validate appUrl
  try {
    new URL(config.appUrl)
  } catch {
    errors.push('appUrl must be a valid URL')
  }

  // Validate providers
  if (!config.providers || config.providers.length === 0) {
    errors.push('At least one provider is required')
  } else {
    config.providers.forEach((provider, index) => {
      if (!provider.id) {
        errors.push(`Provider at index ${index} must have an id`)
      }
      if (!provider.type) {
        errors.push(`Provider at index ${index} must have a type`)
      }
      if (provider.type === 'oauth') {
        if (!provider.clientId) {
          errors.push(`OAuth provider ${provider.id} must have a clientId`)
        }
        if (!provider.clientSecret) {
          errors.push(`OAuth provider ${provider.id} must have a clientSecret`)
        }
      }
    })
  }

  // Validate storage
  if (!config.storage) {
    errors.push('Storage configuration is required')
  } else {
    if (!config.storage.driver) {
      errors.push('Storage driver is required')
    }
    if (config.storage.driver !== 'memory' && !config.storage.url) {
      errors.push('Storage URL is required for non-memory drivers')
    }
  }

  // Validate billing
  if (config.billing) {
    if (!config.billing.driver) {
      errors.push('Billing driver is required')
    }
    if (!config.billing.products || Object.keys(config.billing.products).length === 0) {
      errors.push('At least one billing product must be defined')
    }
  }

  // Validate API keys
  if (!config.apiKeys) {
    errors.push('API key configuration is required')
  } else {
    if (!config.apiKeys.prefix) {
      errors.push('API key prefix is required')
    }
    if (!config.apiKeys.hash) {
      errors.push('API key hash configuration is required')
    } else {
      if (!config.apiKeys.hash.algo) {
        errors.push('API key hash algorithm is required')
      }
    }
  }

  // Validate sessions
  if (!config.sessions) {
    errors.push('Session configuration is required')
  } else {
    if (!config.sessions.cookieName) {
      errors.push('Session cookie name is required')
    }
    if (!config.sessions.ttlSeconds || config.sessions.ttlSeconds <= 0) {
      errors.push('Session TTL must be a positive number')
    }
  }

  return errors
}

// Environment variable helpers
export function getEnvVar(name: string, defaultValue?: string): string {
  const value = process.env[name]
  if (!value && defaultValue === undefined) {
    throw new Error(`Environment variable ${name} is required`)
  }
  return value || defaultValue!
}

export function getEnvVarAsNumber(name: string, defaultValue?: number): number {
  const value = process.env[name]
  if (!value && defaultValue === undefined) {
    throw new Error(`Environment variable ${name} is required`)
  }
  const parsed = value ? parseInt(value, 10) : defaultValue!
  if (isNaN(parsed)) {
    throw new Error(`Environment variable ${name} must be a valid number`)
  }
  return parsed
}

export function getEnvVarAsBoolean(name: string, defaultValue?: boolean): boolean {
  const value = process.env[name]
  if (!value && defaultValue === undefined) {
    throw new Error(`Environment variable ${name} is required`)
  }
  if (!value) return defaultValue!
  return value.toLowerCase() === 'true' || value === '1'
}
